import type {
    LobbyManagerState,
    WaitingPlayerState } from "@rosette/ts/src/business/api/GameAPI.js";
import {
    CheckCanCreateResponse,
    ClearGameRatingResponse,
    CreateGameResponse,
    GameAPI,
    ListLiveLobbiesResponse,
    RecalculateUserStatsResponse,
} from "@rosette/ts/src/business/api/GameAPI.js";
import { MessageIOContext } from "@rosette/ts/src/business/api/MessageIOContext.js";
import { logDebug } from "@rosette/ts/src/business/debug.js";
import { isJsonDict } from "@rosette/ts/src/util/json.js";
import type { GameSource } from "@rosette/ts/src/business/game/controller/source/GameSource.js";
import { MessageInUploadedGame } from "@rosette/ts/src/business/api/game/MessageInUploadedGame.js";
import { fetchWithThrow } from "./fetchWithThrow.js";
import { Bot } from "@rosette/ts/src/business/game/Bot.js";
import { BotType } from "@rosette/ts/src/business/game/BotType.js";
import { Ruleset } from "@rosette/ts/src/business/game/Ruleset.js";
import { MessageOutAnalyseGame } from "@rosette/ts/src/business/api/analysis/MessageOutAnalyseGame.js";
import { MessageInAnalysedGame } from "@rosette/ts/src/business/api/analysis/MessageInAnalysedGame.js";
import { GameAnalysis } from "@rosette/ts/src/business/analysis/GameAnalysis.js";
import type { Engine } from "@rosette/ts/src/royalur/engine/Engine.js";
import { Optional } from "@rosette/ts/src/util/Optional.js";
import type { Game } from "@rosette/ts/src/royalur/Game.js";
import { GameMode } from "@rosette/ts/src/business/game/GameMode.js";
import type { GameSettings } from "@rosette/ts/src/royalur/model/GameSettings.js";
import type { APIEarnedAchievement } from "@rosette/ts/src/business/api/api_schema.js";


export class WebGameAPI extends GameAPI {

    private readonly context: MessageIOContext;
    private readonly gameServerUrl: string;

    constructor(gameServerUrl: string) {
        super();
        this.context = MessageIOContext.createDefault();
        this.gameServerUrl = gameServerUrl;
    }

    private getAPI(): string {
        return `${this.gameServerUrl}/api`;
    }

    private getWebSocketAPI(): string {
        const url = this.getAPI();
        const protocolEnd = url.indexOf("://");
        const protocol = url.substring(0, protocolEnd);
        const rest = url.substring(protocolEnd + 3);
        const wsProtocol = (protocol === "https" ? "wss" : "ws");
        return `${wsProtocol}://${rest}`;
    }

    getCreateLobbyEndpoint(): string {
        const api = this.getAPI();
        return `${api}/lobby/create`;
    }

    getCheckCanCreateLobbyEndpoint(): string {
        const api = this.getAPI();
        return `${api}/lobby/check_can_create`;
    }

    override async createGame(
        gameMode: GameMode,
        settings: GameSettings,
        botType: BotType | null,
    ): Promise<CreateGameResponse> {
        const endpoint = this.getCreateLobbyEndpoint();
        const response = await fetchWithThrow(endpoint, {
            method: "POST",
            credentials: "include",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                game_mode: gameMode.getID(),
                settings: this.context.getGameNotation().writeGameSettings(settings),
                ...(botType !== null ? {
                    bot_type: botType.getID(),
                } : {}),
            }),
        });

        const contentJson = await response.json();
        return CreateGameResponse.parse(contentJson);
    }

    override async checkCanCreateLobby(
        gameMode: GameMode,
        settings: GameSettings,
        botType: BotType | null,
    ): Promise<CheckCanCreateResponse> {
        const endpoint = this.getCheckCanCreateLobbyEndpoint();
        const response = await fetchWithThrow(endpoint, {
            method: "POST",
            credentials: "include",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                game_mode: gameMode.getID(),
                settings: this.context.getGameNotation().writeGameSettings(settings),
                ...(botType !== null ? {
                    bot_type: botType.getID(),
                } : {}),
            }),
        });

        const contentJson = await response.json();
        return CheckCanCreateResponse.parse(contentJson);
    }

    getRecalculateUserStatsEndpoint(): string {
        const api = this.getAPI();
        return `${api}/users/recalculate_stats`;
    }

    override async recalculateUserStats(userPublicID: string): Promise<void> {
        const endpoint = this.getRecalculateUserStatsEndpoint();
        const response = await fetchWithThrow(endpoint, {
            method: "POST",
            credentials: "include",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                user_public_id: userPublicID,
            }),
        });
        const responseContent = await response.json();
        const parsedResponse = RecalculateUserStatsResponse.parse(responseContent);
        if (parsedResponse.user_public_id === userPublicID)
            return;

        throw new Error("Mismatching user_public_id");
    }

    getUploadGameEndpoint(): string {
        const api = this.getAPI();
        return `${api}/games/upload`;
    }

    override async uploadGame(source: GameSource): Promise<APIEarnedAchievement[]> {
        if (source.lobbyStatus.get().getCompleteGameID())
            return [];

        const requestContent = source.buildUploadGameMessage().write(this.context);
        logDebug("uploadGame request =", requestContent);

        const endpoint = this.getUploadGameEndpoint();
        const response = await fetchWithThrow(endpoint, {
            method: "POST",
            credentials: "include",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
            },
            body: JSON.stringify(requestContent),
        });

        const responseContent = await response.json();
        logDebug("uploadGame response =", responseContent);
        if (!isJsonDict(responseContent))
            throw new Error("Invalid response: Not a JSON dictionary");

        const message = new MessageInUploadedGame();
        message.read(this.context, responseContent);
        const finishedGameID = message.finishedGameID;
        if (finishedGameID === null)
            throw new Error("Missing finishedGameID");

        source.lobbyStatus.map(status => status.withCompleteGameID(finishedGameID));
        console.log(`Successfully uploaded game! ${finishedGameID}`);
        return message.earnedAchievements;
    }

    getAnalyseGameEndpoint(): string {
        const api = this.getAPI();
        return `${api}/games/analyse`;
    }

    override async analyseGame(rules: Engine, gameID: string): Promise<Optional<GameAnalysis>> {
        const settings = rules.getSettings();
        const ruleset = Ruleset.getBySettings(settings);
        if (ruleset === Ruleset.CUSTOM || ruleset.isRetired)
            return Optional.empty();

        const requestContent = new MessageOutAnalyseGame(gameID).write(this.context);
        const endpoint = this.getAnalyseGameEndpoint();
        const response = await fetchWithThrow(endpoint, {
            method: "POST",
            credentials: "include",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
            },
            body: JSON.stringify(requestContent),
        });

        const responseContent = await response.json();
        if (!isJsonDict(responseContent))
            throw new Error("Invalid response: Not a JSON dictionary");

        const message = new MessageInAnalysedGame();
        message.read(this.context, responseContent);
        if (message.gameID !== gameID)
            throw new Error("Mismatching game ID");

        const analysis = message.analysis;
        if (analysis === null)
            throw new Error("Missing analysis");

        return Optional.of(GameAnalysis.create(
            rules, this.context, analysis,
        ));
    }

    getGetPandaGameEndpoint(): string {
        const api = this.getAPI();
        return `${api}/games/get_panda_game`;
    }

    override async getPandaGame(): Promise<Game> {
        const endpoint = this.getGetPandaGameEndpoint();
        const response = await fetchWithThrow(endpoint, {
            method: "GET",
            headers: {
                Accept: "application/json",
            },
        });

        const responseContent = await response.json();
        if (!isJsonDict(responseContent))
            throw new Error("Invalid response: Not a JSON dictionary");

        return this.context.getGameNotation().readGame(responseContent);
    }


    getLobbyManagerStateEndpoint(): string {
        const api = this.getAPI();
        return `${api}/lobby/state`;
    }

    override async getLobbyManagerState(): Promise<LobbyManagerState> {
        const endpoint = this.getLobbyManagerStateEndpoint();
        const responseObject = await fetchWithThrow(endpoint, {
            method: "GET",
            headers: {
                Accept: "application/json",
            },
        });

        const notation = this.context.getLobbyNotation();
        const responseContent = await responseObject.json();
        const response = ListLiveLobbiesResponse.parse(responseContent);

        const availableBots: Bot[] = [];
        for (const bot of response.available_bots) {
            const type = BotType.getByIDOrNull(bot.type);
            const settings = Ruleset.getByIDOrNull(bot.settings);
            if (type === null || settings === null)
                continue;

            availableBots.push(new Bot(type, settings));
        }

        const waitingPlayers: WaitingPlayerState[] = [];
        for (const state of response.waiting_players) {
            waitingPlayers.push({
                mode: GameMode.getByID(state.mode),
                settings: Ruleset.getByID(state.settings),
                waitingCount: state.waiting_count,
            });
        }

        const startedLobbies = notation.readLobbySummaries(response.started_lobbies);
        return {
            availableBots,
            waitingPlayers,
            startedLobbies,
            startedLobbiesIsExhaustive: response.started_lobbies_is_exhaustive,
        };
    }

    override getLobbyWebSocketURL(lobbyID: string): string {
        const api = this.getWebSocketAPI();
        return `${api}/lobby/live/${lobbyID}`;
    }

    override getMatchmakingWebSocketURL(): string {
        const api = this.getWebSocketAPI();
        return `${api}/matchmaking/queue`;
    }

    getClearGameRatingEndpoint(gameID: string): string {
        const api = this.getAPI();
        return `${api}/moderate/game/${gameID}/clear_rating`;
    }

    override async clearGameRating(gameID: string, clearRating: boolean, addRatingBackToLoser: boolean): Promise<ClearGameRatingResponse> {
        const endpoint = this.getClearGameRatingEndpoint(gameID);
        const response = await fetchWithThrow(endpoint, {
            method: "POST",
            credentials: "include",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                clear_rating: clearRating,
                add_rating_back_to_loser: addRatingBackToLoser,
            }),
        });

        const responseContent = await response.json();
        return ClearGameRatingResponse.parse(responseContent);
    }
}
